import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  UploadCloud, 
  File as FileIcon, 
  X, 
  Loader2, 
  CheckCircle2, 
  Shield, 
  Send,
  Building2,
  User,
  Mail,
  History,
  AlertCircle,
  Plus,
  Trash2,
  Settings,
  Lock,
  Eye,
  EyeOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SecureTransfer() {
  const [files, setFiles] = useState([]);
  const [patientId, setPatientId] = useState("");
  const [centerOrigin, setCenterOrigin] = useState("");
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recentTransfers, setRecentTransfers] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Recipients management
  const [principalRecipients, setPrincipalRecipients] = useState([]);
  const [ccRecipients, setCcRecipients] = useState([]);
  const [selectedPrincipal, setSelectedPrincipal] = useState([]);
  const [selectedCc, setSelectedCc] = useState([]);
  const [newPrincipalEmail, setNewPrincipalEmail] = useState("");
  const [newCcEmail, setNewCcEmail] = useState("");

  // Admin panel
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [adminConfig, setAdminConfig] = useState(null);
  const [newZipPassword, setNewZipPassword] = useState("");
  const [showZipPassword, setShowZipPassword] = useState(false);
  const [newCenterName, setNewCenterName] = useState("");
  const [isAdminLoading, setIsAdminLoading] = useState(false);

  // Fetch config on mount
  useEffect(() => {
    fetchConfig();
    fetchRecentTransfers();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/config`);
      setConfig(response.data);
      
      // Initialize recipients from config
      const principal = response.data.recipients?.principal || [];
      const cc = response.data.recipients?.cc || [];
      
      setPrincipalRecipients(principal);
      setCcRecipients(cc);
      setSelectedPrincipal(principal);
      setSelectedCc(cc);
    } catch (error) {
      console.error("Error fetching config:", error);
      toast.error("Error al carregar la configuració");
    }
  };

  const fetchRecentTransfers = async () => {
    try {
      const response = await axios.get(`${API}/transfers?limit=5`);
      setRecentTransfers(response.data);
    } catch (error) {
      console.error("Error fetching transfers:", error);
    }
  };

  // Admin functions
  const verifyAdminPassword = async () => {
    setIsAdminLoading(true);
    try {
      const response = await axios.post(`${API}/admin/verify`, { password: adminPassword });
      if (response.data.success) {
        setIsAdminAuthenticated(true);
        await fetchAdminConfig();
        toast.success("Accés autoritzat");
      }
    } catch (error) {
      toast.error("Contrasenya incorrecta");
      setAdminPassword("");
    } finally {
      setIsAdminLoading(false);
    }
  };

  const fetchAdminConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/config?password=${adminPassword}`);
      setAdminConfig(response.data);
      setNewZipPassword(response.data.zip_password || "");
    } catch (error) {
      console.error("Error fetching admin config:", error);
    }
  };

  const updateZipPassword = async () => {
    if (!newZipPassword.trim()) {
      toast.error("La contrasenya no pot estar buida");
      return;
    }
    
    setIsAdminLoading(true);
    try {
      await axios.post(`${API}/admin/config?password=${adminPassword}`, {
        zip_password: newZipPassword
      });
      toast.success("Contrasenya d'encriptació actualitzada");
      await fetchAdminConfig();
    } catch (error) {
      toast.error("Error actualitzant la contrasenya");
    } finally {
      setIsAdminLoading(false);
    }
  };

  const addCenter = async () => {
    if (!newCenterName.trim()) {
      toast.error("El nom del centre no pot estar buit");
      return;
    }
    
    setIsAdminLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/centers/add?center_name=${encodeURIComponent(newCenterName)}&password=${adminPassword}`
      );
      if (response.data.success) {
        toast.success(`Centre "${newCenterName}" afegit`);
        setNewCenterName("");
        await fetchAdminConfig();
        await fetchConfig(); // Refresh main config
      } else {
        toast.warning(response.data.message);
      }
    } catch (error) {
      toast.error("Error afegint el centre");
    } finally {
      setIsAdminLoading(false);
    }
  };

  const removeCenter = async (centerName) => {
    setIsAdminLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/centers/remove?center_name=${encodeURIComponent(centerName)}&password=${adminPassword}`
      );
      if (response.data.success) {
        toast.success(`Centre "${centerName}" eliminat`);
        await fetchAdminConfig();
        await fetchConfig(); // Refresh main config
      }
    } catch (error) {
      toast.error("Error eliminant el centre");
    } finally {
      setIsAdminLoading(false);
    }
  };

  const closeAdminDialog = () => {
    setShowAdminDialog(false);
    setIsAdminAuthenticated(false);
    setAdminPassword("");
    setAdminConfig(null);
  };

  // Email validation
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Add new principal recipient
  const addPrincipalRecipient = () => {
    if (newPrincipalEmail && isValidEmail(newPrincipalEmail)) {
      if (!principalRecipients.includes(newPrincipalEmail)) {
        const updated = [...principalRecipients, newPrincipalEmail];
        setPrincipalRecipients(updated);
        setSelectedPrincipal([...selectedPrincipal, newPrincipalEmail]);
        setNewPrincipalEmail("");
        saveRecipients(updated, ccRecipients);
      } else {
        toast.warning("Aquest email ja existeix");
      }
    } else {
      toast.error("Introdueix un email vàlid");
    }
  };

  // Add new CC recipient
  const addCcRecipient = () => {
    if (newCcEmail && isValidEmail(newCcEmail)) {
      if (!ccRecipients.includes(newCcEmail)) {
        const updated = [...ccRecipients, newCcEmail];
        setCcRecipients(updated);
        setSelectedCc([...selectedCc, newCcEmail]);
        setNewCcEmail("");
        saveRecipients(principalRecipients, updated);
      } else {
        toast.warning("Aquest email ja existeix");
      }
    } else {
      toast.error("Introdueix un email vàlid");
    }
  };

  // Remove recipient
  const removePrincipalRecipient = (email) => {
    const updated = principalRecipients.filter(e => e !== email);
    setPrincipalRecipients(updated);
    setSelectedPrincipal(selectedPrincipal.filter(e => e !== email));
    saveRecipients(updated, ccRecipients);
  };

  const removeCcRecipient = (email) => {
    const updated = ccRecipients.filter(e => e !== email);
    setCcRecipients(updated);
    setSelectedCc(selectedCc.filter(e => e !== email));
    saveRecipients(principalRecipients, updated);
  };

  // Save recipients to backend
  const saveRecipients = async (principal, cc) => {
    try {
      await axios.post(`${API}/config/recipients`, {
        principal,
        cc
      });
    } catch (error) {
      console.error("Error saving recipients:", error);
    }
  };

  // Toggle recipient selection
  const togglePrincipalSelection = (email) => {
    if (selectedPrincipal.includes(email)) {
      setSelectedPrincipal(selectedPrincipal.filter(e => e !== email));
    } else {
      setSelectedPrincipal([...selectedPrincipal, email]);
    }
  };

  const toggleCcSelection = (email) => {
    if (selectedCc.includes(email)) {
      setSelectedCc(selectedCc.filter(e => e !== email));
    } else {
      setSelectedCc([...selectedCc, email]);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      setFiles(prev => [...prev, ...droppedFiles]);
    }
  }, []);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    setFiles([]);
    setPatientId("");
    setCenterOrigin("");
    setUploadProgress(0);
  };

  const getTotalSize = () => {
    const totalBytes = files.reduce((acc, file) => acc + file.size, 0);
    return (totalBytes / (1024 * 1024)).toFixed(2);
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    if (imageExts.includes(ext)) {
      return '🖼️';
    }
    const docExts = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
    if (docExts.includes(ext)) {
      return '📄';
    }
    const videoExts = ['mp4', 'avi', 'mov', 'mkv'];
    if (videoExts.includes(ext)) {
      return '🎬';
    }
    return '📁';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (files.length === 0) {
      toast.error("Selecciona almenys un arxiu");
      return;
    }

    if (!patientId.trim()) {
      toast.error("Introdueix l'identificador de l'estudi");
      return;
    }

    if (!centerOrigin) {
      toast.error("Selecciona el centre d'origen");
      return;
    }

    if (selectedPrincipal.length === 0) {
      toast.error("Selecciona almenys un destinatari principal");
      return;
    }

    setIsLoading(true);
    setUploadProgress(10);

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    formData.append('patient_id', patientId.trim());
    formData.append('center_origin', centerOrigin);
    formData.append('recipients_to', JSON.stringify(selectedPrincipal));
    formData.append('recipients_cc', JSON.stringify(selectedCc));

    try {
      setUploadProgress(30);
      
      const response = await axios.post(`${API}/transfer`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 50) / progressEvent.total
          );
          setUploadProgress(30 + percentCompleted);
        },
      });

      setUploadProgress(100);

      if (response.data.success) {
        toast.success("Arxius enviats correctament!", {
          description: response.data.message
        });
        clearAll();
        fetchRecentTransfers();
      }
    } catch (error) {
      console.error("Error sending files:", error);
      const errorMessage = error.response?.data?.detail || "Error en l'enviament";
      toast.error("Error en l'enviament", {
        description: errorMessage
      });
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ca-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-white bg-mesh">
      {/* Header */}
      <header className="border-b border-slate-100 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_479e06de-6f4d-49cc-80b3-fdf84d88e96e/artifacts/wpw46h1g_Logo.png" 
              alt="Gabimedi" 
              className="h-10 object-contain"
              data-testid="logo-gabimedi"
            />
            <div className="h-8 w-px bg-slate-200" />
            <span className="text-slate-500 text-sm font-medium">Transferència Segura</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
              className="text-slate-600 hover:text-slate-900 relative z-20"
              data-testid="toggle-history-btn"
            >
              <History className="w-4 h-4 mr-2" />
              Historial
            </Button>
            
            {/* Admin Button */}
            <Dialog open={showAdminDialog} onOpenChange={(open) => {
              if (!open) closeAdminDialog();
              else setShowAdminDialog(true);
            }}>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-600 hover:text-slate-900"
                  data-testid="admin-btn"
                >
                  <Settings className="w-4 h-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    Configuració Administrador
                  </DialogTitle>
                  <DialogDescription>
                    {!isAdminAuthenticated 
                      ? "Introdueix la contrasenya d'administrador per accedir"
                      : "Gestiona la contrasenya d'encriptació i els centres"}
                  </DialogDescription>
                </DialogHeader>

                {!isAdminAuthenticated ? (
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="admin-password">Contrasenya d'administrador</Label>
                      <div className="flex gap-2">
                        <Input
                          id="admin-password"
                          type="password"
                          value={adminPassword}
                          onChange={(e) => setAdminPassword(e.target.value)}
                          placeholder="Introdueix la contrasenya"
                          onKeyPress={(e) => e.key === 'Enter' && verifyAdminPassword()}
                          data-testid="admin-password-input"
                        />
                        <Button 
                          onClick={verifyAdminPassword}
                          disabled={isAdminLoading || !adminPassword}
                          data-testid="admin-login-btn"
                        >
                          {isAdminLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accedir"}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6 py-4">
                    {/* ZIP Password Section */}
                    <div className="space-y-3">
                      <Label className="text-sm font-semibold">Contrasenya d'encriptació (7z)</Label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Input
                            type={showZipPassword ? "text" : "password"}
                            value={newZipPassword}
                            onChange={(e) => setNewZipPassword(e.target.value)}
                            placeholder="Nova contrasenya"
                            data-testid="zip-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowZipPassword(!showZipPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            {showZipPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <Button 
                          onClick={updateZipPassword}
                          disabled={isAdminLoading}
                          size="sm"
                          data-testid="update-zip-password-btn"
                        >
                          {isAdminLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">
                        Actual: <code className="bg-slate-100 px-1 rounded">{showZipPassword ? adminConfig?.zip_password : '••••••••'}</code>
                      </p>
                    </div>

                    {/* Centers Section */}
                    <div className="space-y-3">
                      <Label className="text-sm font-semibold">Centres d'origen</Label>
                      <div className="space-y-2">
                        {adminConfig?.centers?.map((center) => (
                          <div key={center} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                            <span className="text-sm">{center}</span>
                            <button
                              onClick={() => removeCenter(center)}
                              className="p-1 hover:bg-red-100 rounded transition-colors"
                              disabled={isAdminLoading}
                              data-testid={`remove-center-${center}`}
                            >
                              <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          value={newCenterName}
                          onChange={(e) => setNewCenterName(e.target.value)}
                          placeholder="Nou centre (ex: Centre Barcelona)"
                          onKeyPress={(e) => e.key === 'Enter' && addCenter()}
                          data-testid="new-center-input"
                        />
                        <Button 
                          onClick={addCenter}
                          disabled={isAdminLoading || !newCenterName.trim()}
                          size="sm"
                          data-testid="add-center-btn"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                <DialogFooter>
                  <Button variant="outline" onClick={closeAdminDialog}>
                    Tancar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Title Section */}
        <div className="text-center mb-8">
          <h1 
            className="text-4xl md:text-5xl font-light tracking-tight text-slate-900 mb-3"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Transferència Segura
          </h1>
          <p className="text-base text-slate-500 max-w-xl mx-auto">
            Envia arxius de forma segura amb xifrat 7z automàtic
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Form Card */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden card-hover">
              <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
                {/* Upload Zone */}
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3 block">
                    Arxius (qualsevol tipus)
                  </Label>
                  <div
                    className={`
                      relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer
                      transition-all duration-300 ease-out
                      ${isDragging 
                        ? 'border-[#0066B3] bg-blue-50 ring-4 ring-blue-100 upload-zone-active' 
                        : 'border-slate-200 bg-slate-50 hover:border-[#0066B3]/50 hover:bg-blue-50/50'
                      }
                    `}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('file-input').click()}
                    data-testid="upload-zone"
                    role="button"
                    aria-label="Zona de pujada d'arxius"
                  >
                    <input
                      id="file-input"
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="file-input"
                    />
                    <UploadCloud 
                      className={`w-10 h-10 mx-auto mb-3 transition-colors duration-300 ${
                        isDragging ? 'text-[#0066B3]' : 'text-slate-300'
                      }`} 
                    />
                    <p className="text-slate-600 font-medium mb-1">
                      Arrossega els arxius aquí
                    </p>
                    <p className="text-slate-400 text-sm">
                      o fes clic per seleccionar (qualsevol tipus d'arxiu)
                    </p>
                  </div>

                  {/* File List */}
                  {files.length > 0 && (
                    <div className="mt-3 space-y-2" data-testid="file-list">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">
                          {files.length} arxiu(s) • {getTotalSize()} MB
                        </span>
                        <button
                          type="button"
                          onClick={() => setFiles([])}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          data-testid="clear-files-btn"
                        >
                          Netejar tot
                        </button>
                      </div>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {files.map((file, index) => (
                          <div
                            key={`${file.name}-${index}`}
                            className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg file-item-enter"
                          >
                            <span className="text-lg">{getFileIcon(file.name)}</span>
                            <span className="flex-1 text-sm text-slate-700 truncate">
                              {file.name}
                            </span>
                            <span className="text-xs text-slate-400">
                              {(file.size / 1024).toFixed(0)} KB
                            </span>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="p-1 hover:bg-slate-200 rounded transition-colors"
                              data-testid={`remove-file-${index}`}
                            >
                              <X className="w-3 h-3 text-slate-400" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Patient ID */}
                <div>
                  <Label 
                    htmlFor="patient-id" 
                    className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 block"
                  >
                    Identificador de l'estudi
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="patient-id"
                      type="text"
                      placeholder="Ex: PAC-2024-001 o nom del pacient"
                      value={patientId}
                      onChange={(e) => setPatientId(e.target.value)}
                      className="h-11 pl-10 bg-white border-slate-200 focus:border-[#0066B3] focus:ring-2 focus:ring-[#0066B3]/20 rounded-lg"
                      data-testid="patient-id-input"
                    />
                  </div>
                </div>

                {/* Center Origin */}
                <div>
                  <Label 
                    htmlFor="center-origin" 
                    className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 block"
                  >
                    Centre d'origen
                  </Label>
                  <Select value={centerOrigin} onValueChange={setCenterOrigin}>
                    <SelectTrigger 
                      className="h-11 bg-white border-slate-200 focus:border-[#0066B3] focus:ring-2 focus:ring-[#0066B3]/20 rounded-lg"
                      data-testid="center-select"
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <SelectValue placeholder="Selecciona el centre" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {config?.centers?.map((center) => (
                        <SelectItem key={center} value={center}>
                          {center}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Progress Bar */}
                {isLoading && (
                  <div className="space-y-2" data-testid="progress-section">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">
                        {uploadProgress < 80 ? 'Pujant i comprimint arxius...' : 'Enviant emails...'}
                      </span>
                      <span className="text-[#0066B3] font-medium">{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} className="h-2 progress-active" />
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isLoading || files.length === 0}
                  className="w-full h-12 bg-[#0066B3] hover:bg-[#005299] text-white shadow-lg shadow-blue-900/20 rounded-full text-lg font-medium btn-primary"
                  data-testid="submit-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processant...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Enviar Estudi
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Security Badge */}
            <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-sm">Xifrat 7z Segur</h3>
                  <p className="text-xs text-slate-500">Protecció amb contrasenya AES-256</p>
                </div>
              </div>
              <p className="text-sm text-slate-600">
                Els arxius es comprimeixen en format 7z amb xifrat AES-256. S'envien 2 correus: un amb els arxius i un altre amb la contrasenya.
              </p>
            </div>

            {/* Principal Recipients */}
            <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-[#0066B3]" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-sm">Destinataris Principals</h3>
                  <p className="text-xs text-slate-500">Selecciona els recipients</p>
                </div>
              </div>
              
              <div className="space-y-2 mb-3">
                {principalRecipients.map((email) => (
                  <div key={email} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                    <Checkbox
                      id={`principal-${email}`}
                      checked={selectedPrincipal.includes(email)}
                      onCheckedChange={() => togglePrincipalSelection(email)}
                      data-testid={`checkbox-principal-${email}`}
                    />
                    <label 
                      htmlFor={`principal-${email}`}
                      className="flex-1 text-xs text-slate-600 cursor-pointer truncate"
                    >
                      {email}
                    </label>
                    <button
                      type="button"
                      onClick={() => removePrincipalRecipient(email)}
                      className="p-1 hover:bg-red-100 rounded transition-colors"
                      data-testid={`remove-principal-${email}`}
                    >
                      <Trash2 className="w-3 h-3 text-slate-400 hover:text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="nou@email.com"
                  value={newPrincipalEmail}
                  onChange={(e) => setNewPrincipalEmail(e.target.value)}
                  className="h-8 text-xs"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPrincipalRecipient())}
                  data-testid="new-principal-input"
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={addPrincipalRecipient}
                  className="h-8 px-2 bg-[#0066B3]"
                  data-testid="add-principal-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* CC Recipients */}
            <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-slate-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-sm">Amb Còpia (CC)</h3>
                  <p className="text-xs text-slate-500">Destinataris secundaris</p>
                </div>
              </div>
              
              <div className="space-y-2 mb-3">
                {ccRecipients.map((email) => (
                  <div key={email} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                    <Checkbox
                      id={`cc-${email}`}
                      checked={selectedCc.includes(email)}
                      onCheckedChange={() => toggleCcSelection(email)}
                      data-testid={`checkbox-cc-${email}`}
                    />
                    <label 
                      htmlFor={`cc-${email}`}
                      className="flex-1 text-xs text-slate-600 cursor-pointer truncate"
                    >
                      {email}
                    </label>
                    <button
                      type="button"
                      onClick={() => removeCcRecipient(email)}
                      className="p-1 hover:bg-red-100 rounded transition-colors"
                      data-testid={`remove-cc-${email}`}
                    >
                      <Trash2 className="w-3 h-3 text-slate-400 hover:text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="nou@email.com"
                  value={newCcEmail}
                  onChange={(e) => setNewCcEmail(e.target.value)}
                  className="h-8 text-xs"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCcRecipient())}
                  data-testid="new-cc-input"
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={addCcRecipient}
                  className="h-8 px-2 bg-slate-500"
                  data-testid="add-cc-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Recent History */}
            {showHistory && recentTransfers.length > 0 && (
              <div 
                className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 p-5"
                data-testid="history-panel"
              >
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2 text-sm">
                  <History className="w-4 h-4" />
                  Enviaments Recents
                </h3>
                <div className="space-y-2">
                  {recentTransfers.map((transfer) => (
                    <div 
                      key={transfer.id}
                      className="p-2 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-xs text-slate-700">
                          {transfer.patient_id}
                        </span>
                        <Badge 
                          variant={transfer.status === 'sent' ? 'default' : 'secondary'}
                          className={`text-xs ${transfer.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : ''}`}
                        >
                          {transfer.status === 'sent' ? (
                            <><CheckCircle2 className="w-3 h-3 mr-1" /> Enviat</>
                          ) : transfer.status === 'error' ? (
                            <><AlertCircle className="w-3 h-3 mr-1" /> Error</>
                          ) : (
                            'Processant'
                          )}
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-500">
                        {transfer.center_origin} • {transfer.file_count} arxius • {transfer.total_parts || 1} part(s)
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        {formatDate(transfer.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 mt-12">
        <div className="max-w-5xl mx-auto px-4 py-4 text-center text-xs text-slate-400">
          © {new Date().getFullYear()} Gabimedi - Sistema de Transferència Segura
        </div>
      </footer>
    </div>
  );
}
